import Promise from 'bluebird';
import _ from 'underscore';
import URI from 'urijs';
import { getGridColumnsProperties } from '@bingads-webui-campaign/inline-download-grids-metadata';
import { setFilterByReportEmailRule } from './set-filter-by-report-email-rule';
import { inlineDownloadGenericPlugin } from './generic-plugin';

/**
 * Will be called with the current grid's FilterEntity and should return additional query
 * parameters, that would normally be included into an OData grid data request when such filter
 * is used in a grid.
 * @callback CustomFilterQueryParamsCallback
 * @param {FilterEntity} - the filter entity representing the current filter
 * @returns {Object} - an object with key/value pairs representing the additional query parameters
 */

/**
 * @typedef InlineDownloadConfig
 * @type {object}
 * @property {string} button - The id of the download button.
 * It must match the download button id defined in toolbar plugin.
 * @property {boolean} isAvailable - Whether the download feature is enabled
 * @property {string[]} [segmentationTypes=segment.getAvailableSegmentationTypes()]
 *  - supported segmentation types, default to be segmentation types defined in segment plugin
 * @property {string} entity
 * @property {string} gridId
 * @property {I18nModel} i18n - i18n
 * @property {SchemaI18n} s$i18n - SchemaI18n to get corresponding localized texts for properties
 *  When this property exists, it will supersede columns, customGridColumns
 *  And correspond information needs to be added to
 *  bingads-webui-campaign/inline-download-grids-metadata.
 * @property {Object.<string, InlineDownloadColumns>} columns -
 *  DEPRECATED: The override column config.
 *  add your column-property mapping into @bingads-webui-campaign/inline-download-grids-metadata
 * @property {function} customGridColumns - DEPRECATED
 *  add your column-property mapping into @bingads-webui-campaign/inline-download-grids-metadata
 * @property {function} processReportRule - a hook before sending final ReportRule to OData server
 *  add all custom data specific for your feature using this method
 * @property {CustomFilterQueryParamsCallback} [customFilterQueryParams] - The
 * callback that returns custom query parameters based on the current filter.
 * Note that for additional query parameter that don't depend on the current
 * filter it is recommended to inject them using a custom processReportRule.
 * See examples below.
 * @example <caption>Injecting custom query parameters</caption>
 * ```javascript
 * const customQueryParams = "Test1=1&Test2=2";
 * const processReportRule = ReportRule => {
 *   ReportRule.Selection = ReportRule.Selection || {};
 *   ReportRule.Selection.QueryString = ReportRule.Selection.QueryString || '';
 *   ReportRule.Selection.QueryString = ReportRule.Selection.QueryString ?
 *     `${ReportRule.Selection.QueryString}&${customQueryParams}` :
 *     customQueryParams;
 * };
 *
 * const pluginsConfig = {
 *   inlineDownload: {
 *     // ...
 *     processReportRule
 *     // ...
 *   }
 * };
 * ```
 * @example <caption>Injecting custom query parameters based on filter</caption>
 * ```javascript
 * const customFilterQueryParams = filterEntity => ({ Test1: 1, Test2: 2 });
 *
 * const pluginsConfig = {
 *   inlineDownload: {
 *     // ...
 *     customFilterQueryParams
 *     // ...
 *   }
 * };
 * ```
 */

/**
 * A hook to change default ReportRule created by this plugin
 * ReportRule is a sub-class of TaskItem, which creates a task to download files
 * @callback ProcessReportRule
 * @param {ReportRule} - the default ReportRule created by this plugin
 * @returns {ReportRule} - the changed ReportRule with data specific for your feature
 */

/**
 * @deprecated
 * @typedef InlineDownloadColumns
 * @type {InlineDownloadColumnConfig|InlineDownloadColumnConfig[]}
 */

/**
 * @deprecated
 * @typedef InlineDownloadColumnConfig
 * @type {object}
 * @property {string} property - The property path in form of 'foo/bar'.
 * @property {string} title - The localized column header string.
 */

export const inlineDownloadCampaignODataPlugin = definePlugin =>
  inlineDownloadGenericPlugin((pluginName, dependencies, callback) => {
    definePlugin(pluginName, dependencies, (config, gridView, toolbar, segment) => {
      const {
        DownloadView,
        schema,
        // TODO: remove these options
        columns = {},
        customGridColumns = null,
        // END TODO
        processReportRule,
        // this option will override columns, customGridColumns,
        // and finally replace them
        s$i18n,
        customDownloadService,
        ReportEmailRule,
        // TODO: [zhbliu] refactor to auto generate it then we can remove this param
        selection: rawSelection,
        // END TODO
        customFilterQueryParams = null,
      } = config.plugins.inlineDownload;
      const { config: appConfig } = config;
      const selection = rawSelection || _.result(ReportEmailRule, 'Selection');
      const { filterService } = config;

      class CampaignODataDownloadView extends DownloadView {
        initialize({
          selectedColumns,

          downloadFormats,
          segmentationTypes,
          entity,
          gridId,
          totalRecordCount,
          validateSegmentType,
          isFeedbackEnabled,
          data,

          p$daterange,
        }) {
          let localizedHeaders;
          let odataColumnNames;
          const currentFilter = filterService.getCombinedFilter();

          if (s$i18n) {
            const properties = getGridColumnsProperties(
              entity,
              _.map(selectedColumns, ({ name }) => name),
              selection
            );
            odataColumnNames = _.map(properties, property => (_.isArray(property) ? property.join('/') : property));
            localizedHeaders = _.map(properties, name => s$i18n.property(schema || entity, name));
          } else {
            const gridColumns = _.isFunction(customGridColumns) ?
              customGridColumns(selectedColumns) :
              selectedColumns;

            [localizedHeaders, odataColumnNames] = _.map([{
              key: 'title',
              getDefault(column) {
                return _.result(column, 'title', column.name);
              },
            }, {
              key: 'property',
              getDefault(column) {
                const property = column.property || column.name;

                return _.isString(property) ? property : property.key;
              },
            }], ({ key, getDefault }) => _.reduce(gridColumns, (memo, column) => {
              const { name } = column;
              const columnConfig = _.result(columns, name);
              const defaultValue = getDefault(column);

              if (_.isArray(columnConfig)) {
                _.each(columnConfig, col => memo.push(_.result(col, key, defaultValue)));
              } else if (_.isObject(columnConfig)) {
                memo.push(_.result(columnConfig, key, defaultValue));
              } else {
                memo.push(defaultValue);
              }

              return memo;
            }, []));
          }

          const filterString = currentFilter ? encodeURIComponent(currentFilter.odataV4({ ignoreCase: false })) : '';
          let filterUrlParam = filterString ? `$filter=${filterString}` : '';

          const filterCustomQueryParams = _.isFunction(customFilterQueryParams) ?
            customFilterQueryParams(currentFilter) : {};
          const additionalFilterUrlParams = URI.buildQuery(filterCustomQueryParams);
          if (additionalFilterUrlParams) {
            filterUrlParam = `${(filterUrlParam ? `${filterUrlParam}&` : '')}${additionalFilterUrlParams}`;
          }

          super.initialize({
            customDownloadService,
            downloadFormats,
            segmentationTypes,
            localizedHeaders,
            odataColumnNames,
            filterUrlParam,
            serializedFilter: filterService.serialize(),
            entity,
            gridId,
            totalRecordCount,
            processReportRule,
            validateSegmentType,
            isFeedbackEnabled,
            data,
            isForVNext: true,
            p$daterange,
          });
        }
      }

      const returns = callback(_.defaults({
        plugins: _.defaults({
          inlineDownload: _.defaults({
            DownloadView: CampaignODataDownloadView,
          }, config.plugins.inlineDownload),
        }, config.plugins),
      }, config), gridView, toolbar, segment);

      function createSimilarReportEmailRule(Rule) {
        Promise.all([
          setFilterByReportEmailRule({
            appConfig,
            filterService,
            ReportEmailRule: Rule,
          }),
          // Make sure grid is loaded then open inline download panel
          new Promise((resolve) => {
            gridView.once('didReload', () => {
              resolve();
            });
          }),
        ]).then(() => {
          returns.remove();
          returns.showDownloadView(Rule);
        });
      }

      if (ReportEmailRule) {
        createSimilarReportEmailRule(ReportEmailRule);
      }


      return _.defaults({
        setReportEmailRule(Rule) {
          createSimilarReportEmailRule(Rule);
        },
      }, returns);
    });
  });
